import React, { Context, createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import LocalizationContext from '../../../../contexts/LocalizationContext';
import { FailedResponse, submitRequest, SuccessResponse } from '../../../../utilities/submitForm';
import { HelperlinkData, IApps, initialDataDto } from '../interfaces/IAppLayouts';
import INotificationResponse, { blankNotification } from 'src/interfaces/INotificationResponse';
import INotification from 'src/interfaces/INotification';
import IFormSubmitResult from 'src/interfaces/IFormSubmitResult';

export interface AccountDetail {
    department: string;
    email: string;
    id?: number;
    employeeNumber: number;
    firstName: string;
    lastName: string;
    jobTitle: string;
    fullName: string;
    officeLocation: string;
    profilePicture: string; // Base64 encoded string
    username: string;
    lang?: string;
    teamLeader?: AccountDetail;
    isSynergy?: boolean;
    isDocuments?: boolean;
    isHealthAndSafety?: boolean;
}
export interface AccountContextState {
    account?: AccountDetail;
    preferences: PreferencesContext;
    apps: IApps[];
    links: HelperlinkData[];
    notificationsAndToken: INotificationResponse;
}
export interface PreferencesContext {
    annualObjectiveNotifEnabled: boolean;
    followUpNotifEnabled: boolean;
    careerNotifEnabled: boolean;
    overallAssessementNotifEnabled: boolean;
    myTeamNotifEnabled: boolean;
}

interface AccountContextData extends AccountContextState {
    getProfile(failed: () => void): void;
    getPreferences(success: (result: PreferencesContext) => void, failed: FailedResponse): void;
    setPreferences(data: PreferencesContext, success: () => void, failed: FailedResponse): void;
    setPreferencesLanguage(lang: string, success: () => void, failed: FailedResponse): void;
    getApps(succes: (result: initialDataDto) => void, failed: FailedResponse): void;
    getNotification(succes: (result: INotificationResponse) => void, failed: FailedResponse): void;
    markNotifAsSeen(id: number, seen: boolean, success: SuccessResponse, failed: FailedResponse): void;
    deleteNotif(id: number | undefined, success: () => void, failed: FailedResponse): void;
}

const defaultState: AccountContextState = {
    preferences: {
        annualObjectiveNotifEnabled: true,
        followUpNotifEnabled: true,
        careerNotifEnabled: true,
        overallAssessementNotifEnabled: true,
        myTeamNotifEnabled: true,
    },
    apps: [],
    links: [],
    notificationsAndToken: blankNotification,
};

interface AccountProviderProps {
    children: ReactNode;
}

export const AccountContext: Context<AccountContextData> = createContext<AccountContextData>({
    ...defaultState,
    getProfile: () => {},
    getPreferences: () => {},
    setPreferences: () => {},
    setPreferencesLanguage: () => {},
    getApps: () => {},
    getNotification: () => {},
    markNotifAsSeen: () => {},
    deleteNotif: () => {},
});

const AccountProvider = ({ children }: AccountProviderProps) => {
    const localization = useContext(LocalizationContext);
    const [state, setState] = useState<AccountContextState>(defaultState);

    const getProfile = useCallback(
        (failed) => {
            fetch(`/api/employee/get-auth-profile?lang=${localization.lang}`, {
                credentials: 'include',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            })
                .then((res) => (res.ok ? res.json() : Promise.reject(res)))
                .then((result) => setState((s: AccountContextState) => ({ ...s, account: { ...result } })))
                .catch(() => failed());
        },
        [localization.lang]
    );
    const getPreferences = useCallback((success: (result: PreferencesContext) => void, failed: FailedResponse) => {
        submitRequest(
            `/api/employee/preferences`,
            undefined,
            undefined,
            undefined,
            (result: PreferencesContext) => {
                setState((s: AccountContextState) => ({ ...s, preferences: result }));
                success(result);
            },
            failed
        );
    }, []);

    const setPreferences = useCallback((data: PreferencesContext, success: () => void, failed: FailedResponse) => {
        submitRequest(
            `/api/employee/preferences`,
            data,
            undefined,
            undefined,
            () => {
                setState((s: AccountContextState) => ({ ...s, preferences: data }));
                success();
            },
            failed,
            'PUT'
        );
    }, []);

    const setPreferencesLanguage = useCallback((lang: string, success: () => void, failed: FailedResponse) => {
        submitRequest(
            `/api/employee/language`,
            lang,
            undefined,
            undefined,
            () => {
                setState((s: AccountContextState) => (s.account === undefined ? s : { ...s, account: { ...s.account, lang } }));
                success();
            },
            failed,
            'PUT'
        );
    }, []);

    const getApps = useCallback((success: (result: initialDataDto) => void, failed: FailedResponse) => {
        submitRequest(
            `/api/employee/appsLayouts`,
            undefined,
            undefined,
            undefined,
            (result: initialDataDto) => {
                setState((s: AccountContextState) => ({ ...s, apps: result.listOfApps, links: result.listOfLinks }));
                success(result);
            },
            failed
        );
    }, []);

    const getNotification = useCallback((success: (result: INotificationResponse) => void, failed: FailedResponse) => {
        submitRequest(
            '/api/notifications/',
            undefined,
            undefined,
            undefined,
            (result: INotificationResponse) => {
                setState((s: AccountContextState) => ({
                    ...s,
                    notificationsAndToken: {
                        ...result,
                        notifications: result.notifications.map((n: INotification) => ({ ...n, payload: JSON.parse(n.payload) })),
                    },
                }));
                success(result);
            },
            failed
        );
    }, []);

    const markNotifAsSeen = useCallback(
        (id: number, seen: boolean, success: SuccessResponse, failed: FailedResponse) => {
            const notif: INotification | undefined = state.notificationsAndToken.notifications.find((n) => n.id === id);
            if (notif === undefined) return;
            submitRequest<IFormSubmitResult>(
                `/api/notifications/markAs/${id}/${seen}`,
                undefined,
                undefined,
                undefined,
                (result: IFormSubmitResult) => {
                    setState((s: AccountContextState) => ({
                        ...s,
                        notificationsAndToken: {
                            ...s.notificationsAndToken,
                            notifications: [...s.notificationsAndToken.notifications.filter((n) => n.id !== id), { ...notif, seen }],
                        },
                    }));
                    success(result);
                },
                (result: IFormSubmitResult) => {
                    failed(result);
                },
                'POST'
            );
        },
        [state.notificationsAndToken.notifications]
    );

    const deleteNotif = useCallback(
        (id: number | undefined, success: () => void, failed: FailedResponse) => {
            submitRequest(
                `/api/notifications${id === undefined ? '' : `?id=${id}`}`,
                undefined,
                undefined,
                undefined,
                () => {
                    setState((s: AccountContextState) => ({
                        ...s,
                        notificationsAndToken: {
                            ...s.notificationsAndToken,
                            notifications: [...(!id ? [] : s.notificationsAndToken.notifications.filter((n) => n.id !== id))],
                        },
                    }));
                    success();
                },
                failed,
                'DELETE'
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.notificationsAndToken.notifications]
    );

    const context = useMemo<AccountContextData>(
        () => ({
            ...state,
            getProfile,
            getPreferences,
            setPreferences,
            setPreferencesLanguage,
            getApps,
            getNotification,
            markNotifAsSeen,
            deleteNotif,
        }),
        [state, getProfile, getPreferences, setPreferences, setPreferencesLanguage, getApps, getNotification, markNotifAsSeen, deleteNotif]
    );

    return <AccountContext.Provider value={context}>{children}</AccountContext.Provider>;
};

export default AccountProvider;
